/**
 * @file MovementCells.tsx
 * @description Ячейки строки журнала движений — количество и «Основание»
 * (phase19.24 этап 1, шаг 2).
 *
 * Две мелкие ячейки лежат рядом, потому что таблица на `lg` и карточки на
 * `<lg` обязаны показывать их одинаково: разъехавшиеся знак количества и
 * подпись документа сразу читаются как разные данные.
 *
 * «Основание» = номер документа ссылкой (маршрут по виду документа держит
 * `MovementDocumentLink`) плюс комментарий движения серым. Комментарий
 * обрезаем: служебные строки бывают длиной в абзац и ломали ширину колонки —
 * полный текст остаётся в `title`. phase19.31: служебный английский текст
 * сервера переводится (`movement-note-text.ts`), партия — коротким номером.
 */

import { cn } from "@/lib/utils";
import { movementNoteView } from "./movement-note-text";
import { MovementDocumentLink, type MovementLinkFit } from "../MovementDocumentLink";
import { shortDocNumber } from "../short-number";
import { formatQtyValue } from "../../../lib/picking-format";
import type { StockMovementDto } from "../../../lib/api-wms";

/** Приход зелёным, расход красным — знак кладовщик читает быстрее слова. */
export function MovementQuantity({
  quantity,
  className,
}: {
  quantity: string;
  className?: string;
}) {
  const value = Number(quantity);
  const positive = !Number.isFinite(value) || value >= 0;
  return (
    <span
      className={cn(
        "shrink-0 font-medium tabular-nums",
        positive ? "text-emerald-600" : "text-destructive",
        className,
      )}
    >
      {value > 0 ? "+" : ""}
      {formatQtyValue(quantity)}
    </span>
  );
}

/**
 * Документ-основание: номер ссылкой, под ним пояснение движения по-русски.
 * `fit="row"` — карточка: ссылка кнопкой-строкой вместе с подписью
 * «Основание:»; `fit="cell"` — таблица: область нажатия 44px без смены вида.
 */
export function MovementBasis({
  movement,
  slug,
  fit = "cell",
}: {
  movement: StockMovementDto;
  slug?: string;
  fit?: Exclude<MovementLinkFit, "inline">;
}) {
  const note = movementNoteView(movement);
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <MovementDocumentLink
        document={movement.document}
        slug={slug}
        fit={fit}
        prefix={fit === "row" ? <span className="shrink-0 text-on-surface-variant">Основание:</span> : undefined}
      />
      {note ? (
        <span className="truncate text-xs text-on-surface-variant" title={note.full}>
          {note.text}
        </span>
      ) : null}
    </span>
  );
}

/** Партия движения коротко («RCP-…018-1»), полный номер — в подсказке. */
export function MovementLot({ movement }: { movement: StockMovementDto }) {
  const full = movement.lot?.batchNumber;
  if (!full) return <span className="text-on-surface-variant">—</span>;
  return (
    <span className="font-mono" title={full} aria-label={`Партия ${full}`}>
      {shortDocNumber(full)}
    </span>
  );
}
